/**
 * @file AuthContext.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Authentication context provider for the Shay Suite Admin application.
 *              This file manages user authentication state, login/logout functionality,
 *              and provides user role-based access control. It handles JWT token parsing,
 *              localStorage persistence, and user session management.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { STORAGE_KEYS, clearAllAuthData, API_CONFIG } from '../helpers/constants';

/**
 * Interface defining the structure of a user object
 * Contains user role, type, company information, and admin privileges
 */
interface User {
  id?: string;            // User ID
  name?: string;          // Display name
  role: string;           // User's role (admin, customer, etc.)
  type?: string;          // User type (customer, admin, etc.)
  companyId?: number;     // Associated company ID (as number)
  companyName?: string;   // Company name
  email?: string;         // User's email address
  isRootAdmin?: boolean;  // Whether user has root admin privileges
}

/**
 * Interface defining the structure of the authentication context
 * Provides authentication state and methods for login/logout operations
 */
interface AuthContextType {
  isAuthenticated: boolean;           // Whether user is currently authenticated
  user: User | null;                  // Current user object or null if not authenticated
  login: (token: string, refreshToken: string, userData?: User) => void;  // Function to log in user
  logout: () => void;                 // Function to log out user
  refreshToken: () => Promise<boolean>; // Function to refresh access token
  isTokenExpired: () => boolean;      // Function to check if token is expired
  token: string | null;               // Current JWT token
  isAdmin: boolean;                   // Whether user has admin role
  isCustomer: boolean;                // Whether user is a customer
  isRootAdmin: boolean;               // Whether user has root admin privileges
}

/**
 * React context for authentication state management
 * Provides authentication data and methods to child components
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to access authentication context
 * 
 * @returns {AuthContextType} The authentication context value
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Props interface for the AuthProvider component
 */
interface AuthProviderProps {
  children: ReactNode;  // Child components to be wrapped by the provider
}

/**
 * Authentication provider component that manages authentication state
 * 
 * This component:
 * - Manages JWT token storage and retrieval
 * - Parses user information from JWT tokens
 * - Provides login/logout functionality
 * - Handles localStorage persistence
 * - Manages user role-based access control
 * 
 * @param {AuthProviderProps} props - Component props containing children
 * @returns {JSX.Element} Context provider wrapping children
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize tokens from localStorage
  const [token, setToken] = useState<string | null>(localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN));
  const [refreshTokenValue, setRefreshTokenValue] = useState<string | null>(localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN));
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);

  /**
   * Effect to parse user data from JWT token and update authentication state
   * Runs whenever the token changes
   */
  useEffect(() => {
    if (token) {
      try {
        // Parse JWT token payload
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Token payload:', payload);
        
        // Extract user data from token payload (name/companyName/email for profile menu)
        const userData = {
          id: payload.id,
          name: payload.name,
          role: payload.role,
          type: payload.type,
          companyId: payload.companyId,
          companyName: payload.companyName,
          email: payload.email,
          isRootAdmin: payload.isRootAdmin,
        };
        console.log('Setting user data:', userData);
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      } catch (error) {
        console.error('Error parsing token:', error);
        logout();
      }
    } else {
      // Clear authentication state if no token
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
  }, [token]);

  /**
   * Function to log in a user with new tokens
   * 
   * @param {string} newToken - JWT token for authentication
   * @param {string} newRefreshToken - Refresh token for token renewal
   * @param {User} userData - Optional user data to set immediately
   */
  const login = (newToken: string, newRefreshToken: string, userData?: User) => {
    console.log('=== AuthContext Login Debug ===');
    console.log('newToken:', newToken ? 'present' : 'missing');
    console.log('newRefreshToken:', newRefreshToken ? 'present' : 'missing');
    console.log('userData:', userData);
    
    setToken(newToken);
    setRefreshTokenValue(newRefreshToken);
    setIsAuthenticated(true);
    if (userData) {
      console.log('Setting user data from userData:', userData);
      setUser(userData);
      
      // Create loggedInUserData for compatibility with existing code
      const loggedInUserData = {
        code: 'S002',
        ownHandler: 'arun',
        message: 'User Logged in successfully',
        resourceId: userData.id || userData.email, // Use email as fallback if id is not available
        actStat: 1,
        adminFlg: userData.role === 'admin' ? 1 : 0,
        category: 'General',
        chat: 0,
        city: null,
        companyData: {
          companyId: userData.companyId,
          companyName: userData.companyName,
          admin: userData.role === 'admin'
        },
        companyId: userData.companyId,
        companyName: userData.companyName,
        countryId: null,
        email: userData.email,
        role: userData.role,
        type: userData.type,
        isRootAdmin: userData.isRootAdmin
      };
      
      localStorage.setItem('loggedInUserData', JSON.stringify(loggedInUserData));
    } else {
      console.log('No userData provided, will parse from token');
    }
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
  };

    /**
   * Function to log out the current user
   * Clears all authentication state and removes tokens from localStorage
   */
  const logout = () => {
    setToken(null);
    setRefreshTokenValue(null);
    setIsAuthenticated(false);
    setUser(null);
    
    // Clear all authentication-related localStorage items
    clearAllAuthData();
  };

  /**
   * Function to check if the current access token is expired
   * 
   * @returns {boolean} True if token is expired, false otherwise
   */
  const isTokenExpired = (): boolean => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  };

  /**
   * Function to refresh the access token using the refresh token
   * 
   * @returns {Promise<boolean>} True if token refresh was successful, false otherwise
   */
  const refreshToken = async (): Promise<boolean> => {
    if (!refreshTokenValue) {
      console.error('No refresh token available');
      return false;
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.code && data.code.startsWith('S')) {
          const { token: newToken, refreshToken: newRefreshToken } = data.data;
          setToken(newToken);
          setRefreshTokenValue(newRefreshToken);
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
          
          // Recreate loggedInUserData after token refresh
          if (user) {
            const loggedInUserData = {
              code: 'S002',
              ownHandler: 'arun',
              message: 'User Logged in successfully',
              resourceId: user.id || user.email, // Use email as fallback if id is not available
              actStat: 1,
              adminFlg: user.role === 'admin' ? 1 : 0,
              category: 'General',
              chat: 0,
              city: null,
              companyData: {
                companyId: user.companyId,
                companyName: user.companyName,
                admin: user.role === 'admin'
              },
              companyId: user.companyId,
              companyName: user.companyName,
              countryId: null,
              email: user.email,
              role: user.role,
              type: user.type,
              isRootAdmin: user.isRootAdmin
            };
            
            localStorage.setItem('loggedInUserData', JSON.stringify(loggedInUserData));
          }
          
          console.log('Token refreshed successfully');
          return true;
        }
      }
      
      console.error('Failed to refresh token');
      return false;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };
  
  // Compute role-based boolean flags
  const isAdmin = user?.role === 'admin';
  const isCustomer = user?.type === 'customer';
  // Root admin is determined by the isRootAdmin flag from JWT token, not by companyId === 1
  const isRootAdmin = user?.isRootAdmin === true;

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      logout, 
      refreshToken, 
      isTokenExpired, 
      token, 
      isAdmin, 
      isCustomer,
      isRootAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 